/** Pure launcher routing helpers — keep Reports OIDC and All-tab SAML from sharing one click path. */

export const NE_EMBED_HOST = 'refex-admin-ui';
export const ITSM_VIRTUAL_ID = 'itsm-inapp';
export const PM_TRACKER_FALLBACK_URL = 'https://pm-tracker-645830234926.asia-south1.run.app/';

export function isNeEmbedApp(app = {}) {
  const url = String(app.home_url || '');
  return url.includes(NE_EMBED_HOST) && (url.includes('embed=1') || url.includes('/applications/') || url.includes('/dashboard'));
}

export function isSamlLauncherApp(app = {}) {
  return String(app?.type || '').trim().toLowerCase() === 'saml';
}

export function isKissflowApp(app = {}) {
  const blob = `${app.name || ''} ${app.description || ''} ${app.home_url || ''} ${app.acs_url || ''} ${app.entity_id || ''}`.toLowerCase();
  return /kissflow|it_service_management/.test(blob);
}

export function isAdrenalinApp(app = {}) {
  const blob = `${app.name || ''} ${app.description || ''} ${app.home_url || ''} ${app.acs_url || ''} ${app.entity_id || ''}`.toLowerCase();
  return /adrenalin|myadrenalin/.test(blob);
}

/** Named ITSM / helpdesk tiles. Never NE embed Reports. */
export function isItsmNamedApp(app = {}) {
  if (isNeEmbedApp(app)) return false;
  const name = (app.name || '').toLowerCase();
  const desc = (app.description || '').toLowerCase();
  const id = (app.id || '').toLowerCase();
  return (
    id === ITSM_VIRTUAL_ID ||
    /itsm|tech support|it support|helpdesk|it helpdesk|it service/.test(name) ||
    /itsm|tech support|it helpdesk|helpdesk/.test(desc)
  );
}

/**
 * Virtual ITSM + All-tab ITSM SAML run the Kissflow-status probe.
 * Reports OIDC must never enter that path (it was opening EMS).
 */
export function shouldHijackItsmLaunch(app = {}) {
  if (!app || isNeEmbedApp(app)) return false;
  if (app.type === 'oidc') return false;
  return isItsmNamedApp(app);
}

/** Project Management Tracker / Project Management tiles (not ITSM, not Reports embeds). */
export function isPmTrackerNamedApp(app = {}) {
  if (!app || isNeEmbedApp(app) || isItsmNamedApp(app)) return false;
  const compact = compactAppName(`${app.name || ''} ${app.description || ''} ${app.home_url || ''}`);
  return (
    compact.includes('projectmanagement')
    || compact.includes('pmtracker')
  );
}

export function shouldHijackPmTrackerLaunch(app = {}) {
  if (!app || isNeEmbedApp(app)) return false;
  return isPmTrackerNamedApp(app);
}

/**
 * GET /itsm/kissflow-status returns HTTP 200 even when Kissflow is down.
 * Use Kissflow's own status_code / ok flag. Not 2xx → in-app /itsm dashboard.
 */
export function isKissflowApiOk(res) {
  const httpOk = Number(res?.status) === 200;
  const data = res?.data || {};
  const kfCode = Number(data.status_code);
  return httpOk && data.ok === true && kfCode >= 200 && kfCode < 300;
}

export function itsmKissflowFallbackReason(res, userInKissflow) {
  if (!isKissflowApiOk(res)) return 'kissflow_unavailable';
  if (!userInKissflow) return 'not_in_kissflow';
  return 'no_sso_target';
}

function usableLauncherApp(app) {
  return Boolean(
    app
    && app.has_access !== false
    && !app.policy_blocked
    && !app.is_placeholder
    && app.id !== ITSM_VIRTUAL_ID,
  );
}

const HONORIFIC_TITLE_RE = /^(mr|mrs|ms|miss|dr|sir|madam|mx)\.?$/i;
const CHIEF_TITLE_RE = /\bchief\b|\bceo\b|\bcfo\b|\bcoo\b|\bcto\b|\bcio\b|\bcmo\b|\bchro\b|\bciso\b|\bcxo\b/i;

export function isReportsLauncherApp(app = {}) {
  if ((app.category || '').trim() === 'Reports') return true;
  return isNeEmbedApp(app);
}

const REPORTS_ALLOWED_EMAILS = [
  'gowtham.s@refex.co.in',
];

const RMC_P2P_EMAILS = [
  'sudharshan.nc@refex.co.in',
  'deepa.murthy@refex.co.in',
  'tarkeshwar.singh@refex.co.in',
  'mounesh.r@refex.co.in',
];

export const AGENTIC_AI_LABEL = 'agentic AI';

const AGENTIC_AI_EMAILS = [
  'aniljain@refex.co.in',
  'yash@refex.co.in',
  'srivaths.varadharajan@refex.co.in',
  'gowtham.s@refex.co.in',
  'aravind.srinivasan@refex.co.in',
];

const compactAppName = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '');

export function isRmcP2pApp(app = {}) {
  const compact = compactAppName(`${app.name || ''} ${app.description || ''}`);
  if (!compact.includes('rmc')) return false;
  return compact.includes('p2p') || compact.includes('procure');
}

export function isProcure2PayApp(app = {}) {
  if (isRmcP2pApp(app) || isReportsLauncherApp(app)) return false;
  const compact = compactAppName(app.name || '');
  return compact.includes('procure2pay') || compact.includes('procurementtopay');
}

function userEmail(user = {}) {
  return String(user?.email || '').trim().toLowerCase();
}

export function userCanSeeRmcP2p(user = {}) {
  if (user?.rmc_p2p_access === true) return true;
  if (user?.rmc_p2p_access === false) return false;
  const email = userEmail(user);
  return Boolean(email) && RMC_P2P_EMAILS.includes(email);
}

export function userCanSeeProcure2Pay(user = {}) {
  if (user?.rmc_p2p_access === true) return false;
  if (user?.rmc_p2p_access === false) return true;
  return !RMC_P2P_EMAILS.includes(userEmail(user));
}

/** Rex / Refex Executive Agent tile. Not the Refexions admin setup page. */
export function isRexApp(app = {}) {
  const id = String(app.id || '').trim().toLowerCase();
  if (id === 'rex') return true;
  const name = String(app.name || '').trim();
  if (/\brex\b/i.test(name) || /agentic\s*ai/i.test(name)) return true;
  const compact = compactAppName(`${name} ${app.description || ''}`);
  return compact.includes('refexexecutiveagent') || compactAppName(name) === 'agenticai';
}

export function launcherAppDisplayName(app = {}) {
  if (isRexApp(app)) return AGENTIC_AI_LABEL;
  if (isItsmNamedApp(app)) return 'Tech Helpdesk';
  return app.name || '';
}

/** Prefix relative `/api/uploads/` logos with the live backend origin. */
export function resolveLauncherLogoUrl(logoUrl, backendOrigin = '') {
  const raw = String(logoUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  const origin = String(backendOrigin || '').replace(/\/$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return origin ? `${origin}${path}` : path;
}

export function userCanSeeAgenticAi(user = {}, app = null) {
  const email = userEmail(user);
  if (email && AGENTIC_AI_EMAILS.includes(email)) return true;
  if (app?.assigned_to_viewer === true) return true;
  const userId = String(user?.id || '');
  const ids = Array.isArray(app?.approved_user_ids) ? app.approved_user_ids.map(String) : [];
  return Boolean(userId) && ids.includes(userId);
}

/** Reports stay on the Reports tab — never mix them into All Apps. */
export function shouldShowOnAllTab(app = {}) {
  return !isReportsLauncherApp(app);
}

function jobTitleFromUser(user = {}) {
  for (const key of ['designation', 'job_title', 'position']) {
    const title = String(user?.[key] || '').trim();
    if (title) return title;
  }
  const title = String(user?.title || '').trim();
  if (title && !HONORIFIC_TITLE_RE.test(title)) return title;
  return '';
}

export function isChiefPosition(user = {}) {
  const parts = [jobTitleFromUser(user), user?.designation, user?.job_title, user?.position];
  const title = String(user?.title || '').trim();
  if (title && !HONORIFIC_TITLE_RE.test(title)) parts.push(title);
  const blob = parts.filter(Boolean).join(' ');
  return Boolean(blob) && CHIEF_TITLE_RE.test(blob);
}

export function userCanSeeReports(user = {}) {
  const email = userEmail(user);
  if (email && REPORTS_ALLOWED_EMAILS.includes(email)) return true;
  return isChiefPosition(user);
}

export function userCanSeeKissflow(_user = {}) {
  return true;
}

export function isKissflowLauncherApp(app = {}) {
  if (isReportsLauncherApp(app)) return false;
  if (isKissflowApp(app)) return true;
  if ((app.id || '') === ITSM_VIRTUAL_ID) return true;
  return isItsmNamedApp(app);
}

export function filterLauncherAppsForUser(apps, user) {
  const list = Array.isArray(apps) ? apps : [];
  return list.filter((app) => {
    if (isReportsLauncherApp(app) && !userCanSeeReports(user)) return false;
    if (isRmcP2pApp(app) && !userCanSeeRmcP2p(user)) return false;
    if (isProcure2PayApp(app) && !userCanSeeProcure2Pay(user)) return false;
    if (isRexApp(app) && !userCanSeeAgenticAi(user, app)) return false;
    return true;
  });
}

/** @deprecated use filterLauncherAppsForUser */
export function filterReportsForUser(apps, user) {
  return filterLauncherAppsForUser(apps, user);
}

/**
 * Resolve which Kissflow SAML app the virtual ITSM tile should open.
 * Prefer the tapped app, then an ITSM-named Kissflow SAML row — never the first
 * Kissflow module in the list (that was sending ITSM clicks to EMS_001_A00).
 */
/**
 * Same probe as the Help Desk KPI tile: Kissflow up + user in Kissflow → SAML app;
 * otherwise the in-app /itsm dashboard.
 */
export function resolveItsmLaunchFromKissflowStatus(res, apps = [], tappedApp = null) {
  const userInKissflow = res?.data?.user_in_kissflow === true;
  if (isKissflowApiOk(res) && userInKissflow) {
    const target = resolveKissflowLaunchApp(apps, tappedApp);
    if (target) return { mode: 'kissflow', app: target };
    return { mode: 'itsm', reason: 'no_sso_target' };
  }
  return { mode: 'itsm', reason: itsmKissflowFallbackReason(res, userInKissflow) };
}

export function resolveKissflowLaunchApp(list, tappedApp) {
  const rows = Array.isArray(list) ? list : [];
  // Tech Helpdesk / ITSM SAML must win even when the tile name has no "kissflow".
  if (tappedApp && usableLauncherApp(tappedApp) && isSamlLauncherApp(tappedApp)) {
    if (isKissflowApp(tappedApp) || isItsmNamedApp(tappedApp)) return tappedApp;
  }
  const itsmSaml = rows.find(
    (a) => usableLauncherApp(a) && isSamlLauncherApp(a) && isItsmNamedApp(a),
  );
  if (itsmSaml) return itsmSaml;
  const itsmKissflow = rows.find(
    (a) => usableLauncherApp(a) && isSamlLauncherApp(a) && isKissflowApp(a) && isItsmNamedApp(a),
  );
  if (itsmKissflow) return itsmKissflow;
  return null;
}

/** Prefer a PM-named Kissflow SAML module so tracker clicks never open EMS. */
export function resolvePmTrackerKissflowLaunchApp(list, tappedApp) {
  const rows = Array.isArray(list) ? list : [];
  if (
    tappedApp
    && usableLauncherApp(tappedApp)
    && tappedApp.type === 'saml'
    && isKissflowApp(tappedApp)
    && isPmTrackerNamedApp(tappedApp)
  ) {
    return tappedApp;
  }
  return rows.find(
    (a) => usableLauncherApp(a) && a.type === 'saml' && isKissflowApp(a) && isPmTrackerNamedApp(a),
  ) || null;
}

function resolveAnyKissflowSamlApp(list) {
  return (Array.isArray(list) ? list : []).find(
    (a) => usableLauncherApp(a) && a.type === 'saml' && isKissflowApp(a),
  ) || null;
}

/**
 * Same Kissflow-status probe as Help Desk:
 * in Kissflow → SSO to the PM Kissflow module (or Kissflow login if no module);
 * not in Kissflow / Kissflow down → Cloud Run tracker.
 */
export function resolvePmTrackerLaunchFromKissflowStatus(res, apps = [], tappedApp = null) {
  const userInKissflow = res?.data?.user_in_kissflow === true;
  if (isKissflowApiOk(res) && userInKissflow) {
    const moduleApp = resolvePmTrackerKissflowLaunchApp(apps, tappedApp);
    if (moduleApp) return { mode: 'kissflow', app: moduleApp };
    const ssoApp = resolveAnyKissflowSamlApp(apps);
    if (ssoApp) return { mode: 'kissflow', app: { ...ssoApp, home_url: '' } };
    return { mode: 'fallback', url: PM_TRACKER_FALLBACK_URL, reason: 'no_sso_target' };
  }
  return {
    mode: 'fallback',
    url: PM_TRACKER_FALLBACK_URL,
    reason: itsmKissflowFallbackReason(res, userInKissflow),
  };
}
